import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Default sections
import { ComplexCommissionTier, GrowthBonusDefinition, GrowthBonusTier } from "@/PayPlans/helpers/DefaultSections";

// Utils
import { isSomeConfiguredModel } from "./utils";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { getErrorKey } from "@/Common/helpers/Modifiers/characters";
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { isSimilarObject } from "@/Common/helpers/Verifications/objects";
import { removeListValue } from "@/Common/helpers/Arrays";

// Matrix helpers
import { getMatrixDimensions, getExactMatrixDimensions, getSmallestBonusMatrixDimensions } from "@/Common/helpers/Matrices";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Custom components
import Content from "./Content";

// MUI
import { Grid } from "@mui/material";

const minimumInput = -100;
const maximumInput = 1000;

const preOwnedTab = "Pre-Owned Vehicles";

const mapDefinitionsToArray = (definition = null) => {
	const result = [];
	if (definition) {
		for (const [key, value] of Object.entries(definition)) {
			const valueType = (typeof value).toUpperCase();
			if (key && !Array.isArray(value) && valueType === "OBJECT") {
				result.push(value);
			}
		}
	}
	return result;
};

const filterErrorsList = (errors, key) => errors.filter((error) => error !== key);
const getGrowthMakeTabs = (makes = []) => makes?.map((element) => element?.make || preOwnedTab);

const getCombinedMakes = (makes = [], franchises = [], content, combineFranchises = false) => {
	const combined = [];
	let additionalMakes = [];
	if (combineFranchises) {
		const isNewIncluded = makes?.some((element) => element.make === "New Vehicles");
		if (!isNewIncluded) {
			combined.push(
				GrowthBonusDefinition(
					"New Vehicles",
					[
						ComplexCommissionTier([minimumInput, "Percent"], [maximumInput, "Percent"], {
							identifier: "bonuses",
							commissions: [GrowthBonusTier(minimumInput, maximumInput, { bonus: 0, input: "Percent" })],
						}),
					],
					{
						frequency: "MoM",
					}
				)
			);
		}

		additionalMakes = makes?.filter((element) => !franchises.includes(element.make));
	} else {
		const contentOriginalList = mapDefinitionsToArray(content);
		if (franchises?.length > 1) {
			franchises.forEach((franchise) => {
				combined.push(
					GrowthBonusDefinition(franchise, [
						ComplexCommissionTier([minimumInput, "Percent"], [maximumInput, "Percent"], {
							identifier: "bonuses",
							commissions: [GrowthBonusTier(minimumInput, maximumInput, { bonus: 0, input: "Percent" })],
						}),
					])
				);
			});
		} else {
			combined.push(makes[0]);
		}

		contentOriginalList?.forEach((element) => {
			const bonusToKeep = makes.find((bonus) => bonus.make === element.make);
			if (bonusToKeep) {
				combined.push(bonusToKeep);
			}
		});
	}
	return [...combined, ...additionalMakes];
};

const getPotentialTabsErrorsKeys = (section, tabs = []) => tabs?.map((tab) => getErrorKey(section, tab));

const getErrorsToRemove = (errors = [], errorsToKeep = []) => {
	if (errors.length) {
		return errors.filter((key) => !errorsToKeep.includes(key));
	}
	return [];
};

const removePreOwnedMake = (makes = []) => makes.filter((make) => make.make !== preOwnedTab);

const getPreOwnedMake = (makes = []) => makes.find((make) => make.make === preOwnedTab);

const mapModelToPropagate = (makes = [], tabs = null) => {
	const preOwned = getPreOwnedMake(makes);
	const makesToSend = removePreOwnedMake(makes);
	return {
		makes: makesToSend,
		preOwned: preOwned || null,
		combineFranchises: Boolean(tabs?.combineFranchises),
		newAndPreOwnedRequired: Boolean(tabs?.newAndPreOwnedRequired),
	};
};

const isOnlyFranchiseInOEM = (oems, franchiseName) => {
	const correspondingOEM = oems?.find((oem) => oem?.franchises?.some((franchise) => franchise?.franchise === franchiseName));
	return correspondingOEM?.franchises?.length === 1;
};

export default function GrowthBonusMakes(props) {
	const {
		sectionId = "growthBonus",
		content = {
			makes: [],
			preOwned: null,
			newAndPreOwnedRequired: false,
			combineFranchises: false,
		},
		oems = [],
		franchises = [],
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { errors, addSectionError, addSectionErrors, removeSectionError, removeSectionErrors, clearSectionErrors } =
		useSectionsErrors?.() || {};

	// States
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
	const [isValidModels, setIsValidModels] = useState(true);
	const [model, setModel] = useState(null);
	const [tabIndex, setTabIndex] = useState(0);
	const [checkBoxesMapErrors, setCheckBoxesMapErrors] = useState({});
	const componentErrors = useRef({});

	const preventSave = useRef([]);

	const matrixDimensions = getMatrixDimensions("bonuses", model?.makes || []);

	const resizeMakes = (makes, newMatrixDimensions, expand = true, shrink = true) => {
		if (expand || shrink) {
			makes.forEach((make, key) => {
				const matrixDimensionsForMake = getExactMatrixDimensions("bonuses", make?.tiers);
				if (!isSimilarObject(newMatrixDimensions, matrixDimensionsForMake)) {
					const tiersDifference = newMatrixDimensions.tiers - matrixDimensionsForMake.tiers;
					const columnsDifference = newMatrixDimensions.columns - matrixDimensionsForMake.columns;
					if (tiersDifference >= 0 && columnsDifference >= 0) {
						// tiers got bigger
						// columns got bigger
						if (expand) {
							make = expandMake(make, newMatrixDimensions.tiers, newMatrixDimensions.columns);
						}
					} else if (tiersDifference <= 0 && columnsDifference <= 0) {
						// tiers got smaller
						// columns got smaller
						if (shrink) {
							make = shrinkMake(make, newMatrixDimensions.tiers, newMatrixDimensions.columns);
						}
					} else if (tiersDifference >= 0 && columnsDifference <= 0) {
						// tiers got bigger
						// columns got smaller
						if (expand) {
							make = expandMake(make, newMatrixDimensions.tiers, matrixDimensionsForMake.columns);
						}
						if (shrink) {
							make = shrinkMake(make, newMatrixDimensions.tiers, newMatrixDimensions.columns);
						}
					} else if (tiersDifference <= 0 && columnsDifference >= 0) {
						// tiers got smaller
						// columns got bigger
						if (shrink) {
							make = shrinkMake(make, newMatrixDimensions.tiers, matrixDimensionsForMake.columns);
						}
						if (expand) {
							make = expandMake(make, newMatrixDimensions.tiers, newMatrixDimensions.columns);
						}
					}
				}
				makes[key] = make;
			});
		}
		return clone(makes);
	};

	const shrinkMake = (make, maxTiers = 1, maxColumns = 1) => {
		// Reducing rows tiers
		make.tiers.length = maxTiers;
		make.tiers[maxTiers - 1].to.value = maximumInput;

		for (const tiers of make?.tiers || []) {
			const tierBonuses = tiers.bonuses;
			// Reducing columns tiers
			tierBonuses.length = maxColumns;
			tierBonuses[maxColumns - 1].to.value = maximumInput;
		}
		return clone(make);
	};

	const expandMake = (make, maxTiers = 1, maxColumns = 1) => {
		if (make?.tiers?.length < maxTiers) {
			preventSave.current.push(getErrorKey(sectionId, make?.make));
			make.tiers[make.tiers.length - 1].to.value = minimumInput;
			while (make?.tiers?.length < maxTiers) {
				const newTier = clone(make.tiers[make.tiers.length - 1]);
				newTier.from.value = minimumInput + 0.01;
				newTier.bonuses.forEach((bonus) => {
					bonus.bonus.value = 0;
				});
				make?.tiers?.push(newTier);
			}
			make.tiers[make.tiers.length - 1].to.value = maximumInput;
		}

		for (const tiers of make.tiers) {
			const tierBonuses = tiers?.bonuses;
			if (tierBonuses?.length < maxColumns) {
				preventSave.current.push(getErrorKey(sectionId, make?.make));
				tierBonuses[tierBonuses.length - 1].to.value = minimumInput;
				const newTierBonus = clone(tierBonuses[tierBonuses.length - 1]);
				newTierBonus.from.value = minimumInput;
				newTierBonus.bonus.value = 0;
				while (tierBonuses?.length < maxColumns) {
					tierBonuses.push(clone(newTierBonus));
				}
			}
			tierBonuses[tierBonuses.length - 1].to.value = maximumInput;
		}

		return clone(make);
	};

	const listErrors = errors?.get(sectionId) || []; // Ids that have errors

	// Error handler
	const tabsErrors = useMemo(() => {
		if (listErrors?.length && model?.tabsProps?.tabs) {
			const localErrors = Array.from(Array(model.tabsProps.tabs.length)).map(() => false);
			const errorsToKeep = getPotentialTabsErrorsKeys(sectionId, model.tabsProps.tabs);

			errorsToKeep.forEach((errorKey, index) => {
				const included = listErrors.includes(errorKey);
				localErrors[index] = Boolean(included);
			});

			return localErrors;
		}
		return [];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sectionId, listErrors, listErrors.length, model?.tabsProps?.tabs]);

	const updateComponentErrors = (bonuses = []) => {
		if (bonuses?.length) {
			const isSomeConfigured = isSomeConfiguredModel({ makes: bonuses });
			componentErrors.current = bonuses
				.filter((item) => item)
				.reduce((acc, { make }) => {
					return {
						...acc,
						[make]: !isSomeConfigured,
					};
				}, {});
		}
	};

	// Actions handlers
	const update = (section, data, area) => {
		const filteredErrors = filterErrorsList(errors?.get(section) || [], getErrorKey(section, area));
		let isValid = !filteredErrors?.length;
		const newAndPreOwnedRequired = Boolean(data?.tabsProps?.newAndPreOwnedRequired);
		if (newAndPreOwnedRequired && isValid) {
			const columns = data?.bonuses?.[0]?.tiers?.[0]?.bonuses?.length;
			const tiers = data?.bonuses?.[0]?.tiers?.length;
			data?.bonuses?.forEach((bonus) => {
				if (bonus?.tiers?.[0]?.bonuses?.length !== columns || bonus?.tiers?.length !== tiers) {
					isValid = false;
				}
			});
		}

		if (isValid && !preventSave?.current?.length) {
			const validatedMakes = data?.makes?.map((make) => {
				const isEnabledFinance = make.isFinance && Boolean(make.tiers?.length > 1);
				return {
					...make,
					isFinance: isEnabledFinance,
				};
			});

			const modelToPropagate = mapModelToPropagate(validatedMakes, data.tabsProps);
			onActionUpdate?.(section, null, modelToPropagate, isValid);
		} else {
			setModel(clone(data));
		}
	};

	const handleUpdateTiers = (section, area, data) => {
		const updatedModel = clone(model);
		const makes = [...updatedModel.makes];

		const currentMatrixDimensions = getExactMatrixDimensions("bonuses", data);
		matrixDimensions.set(area, currentMatrixDimensions);
		preventSave.current = removeListValue(preventSave.current, getErrorKey(sectionId, area));
		removeSectionError(section, area);

		// Search make and update
		makes?.forEach((element, key) => {
			if (element.make === area) {
				makes[key]["tiers"] = data;
			}
		});

		if (model?.tabsProps?.newAndPreOwnedRequired) {
			updatedModel["makes"] = resizeMakes(makes, currentMatrixDimensions, true, true);
		}
		update(section, updatedModel, area);
	};

	const resolveSecondCheck = () => {
		if (model?.makes?.[tabIndex]?.make === preOwnedTab) {
			return "isWholesale";
		} else {
			return "isFleet";
		}
	};

	const isOnlyOneChecked = () => {
		const secondCheckArea = resolveSecondCheck();
		const checkboxList = [
			model?.makes?.[tabIndex]?.isRetail,
			model?.makes?.[tabIndex]?.[secondCheckArea],
			model?.makes?.[tabIndex]?.isFinance,
		];
		return checkboxList?.filter((element) => !element).length === 2;
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	const handleChangeCheckbox = (section, area, currentTabIndex = null) => {
		const updatedModel = { ...model };
		updatedModel.makes[currentTabIndex][area] = Boolean(!updatedModel.makes[currentTabIndex][area]);
		update(section, updatedModel, area);
	};

	const selectCorrectTab = (combineFranchisesSwitchState) => {
		const preOwnedTabActive = model?.makes?.[tabIndex]?.make === preOwnedTab;
		if (combineFranchisesSwitchState) {
			if (preOwnedTabActive) {
				setTabIndex(tabIndex - 1);
			} else {
				setTabIndex(0);
			}
		} else if (preOwnedTabActive) {
			setTabIndex(model.makes.length);
		} else {
			setTabIndex(0);
		}
	};

	const handleUpdateSwitches = (section, area, value, currentTabIndex = null) => {
		const updatedModel = { ...model };

		switch (area) {
			case "applyTiersRetroactively":
			case "frequency":
				if (isNumeric(currentTabIndex)) {
					updatedModel["makes"][currentTabIndex][area] = value;
				}
				break;
			default:
				updatedModel["tabsProps"][area] = value;
				break;
		}

		const combineFranchises = updatedModel["tabsProps"]["combineFranchises"];

		// Closure to combine franchises
		const setCombineFranchises = (combine = false) => {
			const makes = getCombinedMakes(updatedModel["makes"], franchises, content, combine);
			const tabsProps = { ...updatedModel["tabsProps"], tabs: getGrowthMakeTabs([...makes]) };
			updatedModel["tabsProps"] = tabsProps;
			updatedModel["makes"] = makes;
			resetTabsErrors(section, updatedModel["tabsProps"]["tabs"]);
			updateComponentErrors(makes);
		};

		switch (area) {
			case "combineFranchises":
				setCombineFranchises(combineFranchises);
				selectCorrectTab(value);
				break;
			case "newAndPreOwnedRequired": {
				if (franchises?.length > 1 && value) {
					updatedModel["tabsProps"]["combineFranchises"] = true;
				}

				// Combine franchises
				if (value) {
					const makes = getCombinedMakes(
						updatedModel["makes"],
						franchises,
						content,
						updatedModel["tabsProps"]["combineFranchises"]
					);
					const tabsProps = { ...updatedModel["tabsProps"], tabs: getGrowthMakeTabs([...makes]) };
					updatedModel["tabsProps"] = tabsProps;
					const newDimensionsForAllMatrices = getSmallestBonusMatrixDimensions(makes);
					const shrunk = resizeMakes(
						[...makes],
						{
							columns: newDimensionsForAllMatrices?.minColumns,
							tiers: newDimensionsForAllMatrices?.minTiers,
						},
						false,
						true
					);
					updatedModel["makes"] = [...shrunk];
					resetTabsErrors(section, updatedModel["tabsProps"]["tabs"]);
					updateComponentErrors([...shrunk]);
					selectCorrectTab(updatedModel["tabsProps"]["combineFranchises"]);
				}
				break;
			}
			default:
				break;
		}
		update(section, updatedModel, area);
	};

	const resetTabsErrors = useCallback(
		(currentSectionId, tabs) => {
			if (tabs?.length) {
				const errorsToKeep = getPotentialTabsErrorsKeys(currentSectionId, tabs);
				const errorsToRemove = getErrorsToRemove(errors?.get(currentSectionId), errorsToKeep);
				removeSectionErrors(currentSectionId, errorsToRemove);
				addSectionErrors(currentSectionId, errorsToKeep);
			}
		},
		[errors, removeSectionErrors, addSectionErrors]
	);

	useMemo(() => {
		const sectionErrors = errors?.get(sectionId) || [];
		setIsValidModels(!sectionErrors.length);
	}, [sectionId, errors]);

	useEffect(() => {
		const additionalMakes = mapDefinitionsToArray(content);
		const initialMakes = [...content.makes, ...additionalMakes];
		const makes = clone([...initialMakes]);

		setModel({
			tabsProps: {
				tabs: getGrowthMakeTabs([...makes]),
				newAndPreOwnedRequired: Boolean(content.newAndPreOwnedRequired),
				combineFranchises: Boolean(content.combineFranchises),
				errors: [],
			},
			makes,
		});

		if (makes?.length) {
			componentErrors.current = makes.reduce((acc, { make }) => {
				return {
					...acc,
					[make]: true,
				};
			}, {});
		}

		return () => {
			setModel(null);
			preventSave.current = [];
		};
	}, [content]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
			setIsValidModels(true);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					Boolean(model?.makes?.length) && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							tabSelected={tabIndex}
							onTabChange={(index) => setTabIndex(index)}
							errors={tabsErrors}
							content={Object.entries(model?.makes)?.map(([key, value], index) => {
								return (
									<Content
										key={`${key}_${index}`}
										model={model}
										tabIndex={tabIndex}
										handleUpdateSwitches={handleUpdateSwitches}
										sectionId={sectionId}
										isOnlyOneChecked={isOnlyOneChecked}
										readOnly={readOnly}
										handleChangeCheckbox={handleChangeCheckbox}
										resolveSecondCheck={resolveSecondCheck}
										value={value}
										handleUpdateTiers={handleUpdateTiers}
										handleValidation={handleValidation}
										checkBoxesMapErrors={checkBoxesMapErrors}
										setCheckBoxesMapErrors={setCheckBoxesMapErrors}
										componentErrors={componentErrors}
										isOnlyFranchiseInOEM={isOnlyFranchiseInOEM(oems, value?.make)}
									/>
								);
							})}
							topActions={[
								<CustomizedSwitches
									key={"newAndPreOwnedRequired"}
									textLeft={true}
									textChecked={"New and Pre-Owned Required"}
									textUnChecked={"New and Pre-Owned Required"}
									checked={Boolean(model?.tabsProps?.newAndPreOwnedRequired)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "newAndPreOwnedRequired", value)}
									tooltip={{
										title: "Combines franchises if applicable and makes both tabs share the same number of rows and columns.",
										placement: "bottom",
										key: "growth-bonus-new-and-preOwned-tooltip",
									}}
								/>,
								<CustomizedSwitches
									key={"combineFranchises"}
									textLeft={true}
									textChecked={"Combine Franchises"}
									textUnChecked={"Combine Franchises"}
									checked={Boolean(model?.tabsProps?.combineFranchises)}
									disabled={readOnly || Boolean(model?.tabsProps?.newAndPreOwnedRequired) || franchises?.length < 2}
									onChange={(value) => handleUpdateSwitches(sectionId, "combineFranchises", value)}
									tooltip={{
										title: 'Turns all franchise tabs into a "New Vehicles" tab.',
										placement: "bottom",
										key: "growth-bonus-combine-franchises-tooltip",
									}}
								/>,
							]}
							bottomActions={[
								<CustomizedSwitches
									key={"applyTiersRetroactively"}
									textLeft={true}
									textChecked={"Apply retroactively"}
									textUnChecked={"Apply retroactively"}
									checked={Boolean(model?.makes?.[tabIndex]?.applyTiersRetroactively)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "applyTiersRetroactively", value, tabIndex)}
								/>,
							]}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
GrowthBonusMakes.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makes: PropTypes.array,
		preOwned: PropTypes.object,
		newAndPreOwnedRequired: PropTypes.bool,
		combineFranchises: PropTypes.bool,
	}),
	oems: PropTypes.array,
	franchises: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
